<template>
    <DeepiBaseField
        :hasFocus="hasFocus"
        :isValid="select?.isValid ?? true"
        :errorMessages="select?.errorMessages ?? []"
        :label="props.label"
        :hint="props.hint"
        :forceDetails="props.forceDetails"
        :rules="props.rules"
    >
        <v-select
            ref="select"
            class="deepi-select deepi-dropdown"
            variant="solo"
            validateOn="lazy blur"
            menuIcon="icon-arrow-down"
            :flat="true"
            :hideDetails="true"
            :class="classes"
            :menuProps="menuProps"
            :noDataText="$t('shared.components.common.no-data-text')"
            :rules="props.rules"
            :itemTitle="(props.itemTitle as SelectItemKey<T>)"
            :itemValue="(props.itemValue as SelectItemKey<T>)"
            :prefix="props.prefix"
            :placeholder="props.placeholder"
            :items="props.items"
            :multiple="props.multiple"
            :modelValue="(props.modelValue as Val<T, any>)"
			:clearable="false"
            @update:modelValue="emit('update:modelValue', $event)"
            @update:focused="hasFocus = $event"
            @keydown.stop
        >
            <template
                #selection
            >
                <div
                    v-if="props.multiple"
                >
                    <slot
                        v-if="(props.modelValue as any[])?.length === 1"
                        name="selection"
                    >
                        <div
                            class="deepi-row gap-8"
                        >
                            <v-icon
                                v-if="props.items?.find((item) => item[props.itemValue] === (props.modelValue as any[])[0])?.prependIcon"
                                :size="18"
                                :icon="props.items.find((item) => item[props.itemValue] === (props.modelValue as any[])[0])?.prependIcon"
                                :color="props.items.find((item) => item[props.itemValue] === (props.modelValue as any[])[0])?.color"
                            />
                            <p
                                v-title-clamp
                                class="text-body-m text-medium text-ellipsis"
                            >
                                {{ props.items?.find((item) => item[props.itemValue] === (props.modelValue as any[])[0])?.[props.itemTitle] }}
                            </p>
                        </div>
                    </slot>
                    <p
                        v-else
                        v-title-clamp
                        class="text-body-m text-medium text-ellipsis"
                    >
                        {{ $t('shared.components.common.x-items-selected', { count: (props.modelValue as any[])?.length }) }}
                    </p>
                </div>
                <slot
                    v-else
                    name="selection"
                >
                    <v-icon
                        v-if="props.items?.find((item) => item[props.itemValue] === props.modelValue)?.prependIcon"
                        :size="18"
                        :icon="props.items.find((item) => item[props.itemValue] === props.modelValue)?.prependIcon"
                        :color="props.items.find((item) => item[props.itemValue] === props.modelValue)?.color"
                    />
                    <p
                        v-title-clamp
                        class="text-body-m text-medium text-ellipsis"
                    >
                        {{ props.items?.find((item) => item[props.itemValue] === props.modelValue)?.[props.itemTitle] }}
                    </p>
                </slot>
            </template>
            <template
                v-slot:item="{ item, props: itemProps }"
            >
                <v-list-item
                    :class="itemClasses(item.value)"
                    v-bind="{ ...itemProps, title: '' }"
                >
                    <slot
                        name="item"
                        v-bind="item.raw"
                    >
                        <v-icon
                            v-if="item.raw.prependIcon"
                            :size="18"
                            :icon="item.raw.prependIcon"
                            :color="item.raw.color"
                        />
                        <p
                            v-title-clamp
                            class="text-body-m text-medium text-clamp-2"
                        >
                            {{ item.raw[props.itemTitle] }}
                        </p>
                    </slot>
                </v-list-item>
            </template>
			<template
                #prepend-item
            >
  				<slot
                    name="prepend-item"
				/>
			</template>
            <template
				v-if="props.clearable"
				#clear
			>
				<v-icon
					icon="icon-close"
					:size="12"
					@click="onClear"
				/>
			</template>
        </v-select>
    </DeepiBaseField>
</template>

<script setup lang="ts" generic="T extends Record<string, any>">
import { computed, ref } from 'vue';
import { type Dayjs } from 'dayjs';
import { type VSelect } from 'vuetify/components';
import { type SelectItemKey } from 'vuetify/lib/util/helpers.mjs';
import { type Val } from 'vuetify/lib/components/VSelect/VSelect.mjs';

import DeepiBaseField from '../baseField/DeepiBaseField.vue';

export interface DeepiSelectFieldProps<T> {
    label?: string;
    prefix?: string;
    placeholder?: string;
    hint?: string;
    forceDetails?: boolean;
    rules?: ((value: any) => boolean | string)[];
    items?: T[];
    itemTitle?: keyof T;
    itemValue?: keyof T;
    multiple?: boolean;
	clearable?: boolean;
	menuProps?: Record<string, any>;
    modelValue?: string[] | number[] | boolean[] | Dayjs[] | string | number | boolean | Dayjs;
};

const props = withDefaults(defineProps<DeepiSelectFieldProps<T>>(), {
    itemTitle: 'text',
    itemValue: 'value',
	clearable: true
});

const emit = defineEmits(['update:modelValue']);

const select = ref<InstanceType<typeof VSelect>>();

const hasFocus = ref(false);

const classes = computed((): string[] => {
    const classes: string[] = [];
    /// The comparison is important here because isValid is null before first validation
    if (select.value && select.value.isValid === false) {
        classes.push('error');
    }
    return classes;
});

const menuProps = computed(() => {
  const { contentClass, ...menuProps } = props.menuProps ?? {};
  return {
    ...menuProps,
    contentClass: `deepi-dropdown-menu deepi-select-menu ${contentClass ?? ''}`.trim()
  };
});

const itemClasses = (value: number | string): string[] => {
    const classes = ['deepi-dropdown-item'];
    if (props.modelValue === value || (Array.isArray(props.modelValue) && props.modelValue.includes(value as never))) {
        classes.push('active');
    }
    return classes;
};

const onClear = (): void => {
    if (props.multiple) {
        emit('update:modelValue', []);
    }
    else {
        emit('update:modelValue', undefined);
    }
};
</script>

<style scoped lang="scss">
@use 'DeepiSelectField.scss';
</style>